import React, { useCallback, useEffect, useState } from 'react';
import {
	useSelectAuthorization,
	useAppDispatch,
	useSelectRequest,
	useSelectInventory,
	actions,
	useSelectVendors,
	useSelectVendor,
	RequestManager,
} from 'store';
import { SearchBar, Typography, Image, LoadingIndicator, DropdownButton, Pagination } from 'lib/components';
import { InventoriesDataTable } from 'lib/components/DataTables';
import AddItemModal from './AddItemModal';
import { useComprehensiveTopNav, useWindowResize, useCustomToast, usePagination, useSearch } from 'hooks';
import { Inventory, InventoryInput } from 'types';
import routes from 'lib/api/routes';
import classes from './index.module.scss';
import { DropdownItem } from 'lib/components/types';

/**
 * Inventory management component
 * Displays a list of inventory items with filtering, search, and management capabilities
 * Allows creating, updating, and deleting inventory items based on user permissions
 */
const Home = () => {
	// Responsive design helpers
	const { isSmallerDevice } = useWindowResize();
	// Custom toast notifications for user feedback
	const toast = useCustomToast();

	// Redux state and actions
	const dispatch = useAppDispatch();
	// Permission checks for inventory management
	const { canCreateInventory, canUpdateInventory, canDeleteInventory } = useSelectAuthorization();
	// Active API request tracking
	const { active } = useSelectRequest();
	// Inventory data from Redux store
	const inventory = useSelectInventory();
	// Vendors data from Redux store
	const vendors = useSelectVendors();
	// Helper function to get vendor by ID
	const getVendorById = useSelectVendor();

	// Controls visibility of the add/edit item modal
	const [showAddNewItemModal, setShowAddNewItemModal] = useState(false);
	// ID of the currently selected inventory item (for actions like delete)
	const [checked, setChecked] = useState<number[]>([]);
	// Currently selected inventory item (for editing)
	const [checkedItem, setCheckedItem] = useState<Inventory>();
	// Counter to track when data has been loaded
	const [fetchedCount, setFetchedCount] = useState(0);
	// Search query for filtering inventory items
	const [query, setQuery] = useState('');
	// Options for vendor dropdown filter
	const [vendorDropdownItems, setVendorDropdownItems] = useState<DropdownItem<number>[]>([]);
	// Selected vendor filter value (-1 means no filter)
	const [selectedVendorDropdownItem, setSelectedVendorDropdownItem] = useState<number>(-1);

	/**
	 * Fetch inventory and vendors data on component mount
	 */
	useEffect(() => {
		dispatch(actions.fetchInventory());
		dispatch(actions.fetchVendors());
	}, []);

	/**
	 * Update vendor dropdown options when vendors data changes
	 * Transforms vendor data into dropdown item format
	 */
	useEffect(() => {
		setVendorDropdownItems(() => vendors.list.map(({ id, name }) => ({ label: name, value: id })));
		setFetchedCount((prev) => prev + 1);
	}, [vendors.updatedAt]);

	/**
	 * Set up pagination for inventory items list
	 * Uses a custom number of items per page (9)
	 */
	const { isDataFetched, listing, setData, paginatedData, paginationProps } = usePagination(inventory, {
		itemsPerPage: 9,
	});

	/**
	 * Handle search functionality using custom hook
	 * Searches across item name and vendor name
	 */
	useSearch(
		query,
		// Reset function - called when query is empty
		() => {
			setData(listing);
		},
		// Search function - called when query has content
		() => {
			const result = inventory.list.filter(
				(item) =>
					item.name.toLowerCase().search(query.toLowerCase()) !== -1 ||
					getVendorById(item.vendorId)?.name?.toLowerCase().search(query.toLowerCase()) !== -1
			);
			setData(result);
		}
	);

	/**
	 * Monitor API requests for inventory creation, update, and deletion
	 * Shows toast notifications for success/failure and updates UI state accordingly
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		// Handle inventory creation request
		if (RM.req(routes.INVENTORY, 'POST')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
				toggleShowAddNewItemModal();
			}
		}

		// Handle inventory update request
		if (RM.req(routes.INVENTORY_ID(checked[0]), 'PUT')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
				toggleShowAddNewItemModal();
				setCheckedItem(undefined);
				setChecked([]);
			}
		}

		// Handle inventory deletion request
		if (RM.req(routes.INVENTORY_ID(checked[0]), 'DELETE')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
				setCheckedItem(undefined);
				setChecked([]);
			}
		}
	}, [active]);

	/**
	 * Filter inventory items by selected vendor when vendor filter changes
	 * Only applies when a vendor is selected from the dropdown
	 */
	useEffect(() => {
		if (selectedVendorDropdownItem === -1) return;

		const filtered = listing.filter((inventory) => inventory.vendorId === selectedVendorDropdownItem);
		setData(filtered);
	}, [selectedVendorDropdownItem]);

	/**
	 * Open the edit modal when an inventory item is selected for editing
	 */
	useEffect(() => {
		if (!checkedItem) {
			return;
		}

		toggleShowAddNewItemModal();
	}, [checkedItem]);

	/**
	 * Handle vendor filter dropdown selection
	 *
	 * @param value - Selected vendor ID from the dropdown
	 */
	const handleVendorDropdownItemSelect = (value: number) => {
		setSelectedVendorDropdownItem(value);
	};

	/**
	 * Toggle the visibility of the add/edit inventory item modal
	 */
	const toggleShowAddNewItemModal = () => setShowAddNewItemModal((prev) => !prev);

	/**
	 * Handle click on "Add new" button
	 * Resets selection and opens the add item modal
	 */
	const handleAddInventoryOnClick = () => {
		setChecked([]);
		setCheckedItem(undefined);
		toggleShowAddNewItemModal();
	};

	/**
	 * Handle checkbox selection for inventory item rows
	 * Only one item can be selected at a time for actions
	 *
	 * @param id - ID of the inventory item being checked/unchecked
	 * @param isChecked - Whether the checkbox is being checked or unchecked
	 */
	const handleCheckAction = (id: number, isChecked: boolean) => {
		if (isChecked && !checked.includes(id)) {
			setChecked([id]);
			return;
		}

		if (!isChecked || checked.includes(id)) {
			setChecked([]);
		}
	};

	/**
	 * Handle click on an inventory item row
	 * Selects the item for editing if user has update permission
	 *
	 * @param inventory - The inventory item that was clicked
	 */
	const handleRowClick = (inventory: Inventory) => {
		if (canUpdateInventory) {
			setChecked([inventory.id]);
			setCheckedItem(inventory);
		}
	};

	/**
	 * Handle inventory item creation or update action
	 * Dispatches the appropriate Redux action based on the operation type
	 *
	 * @param type - The type of action ('create' or 'update')
	 * @param data - The inventory item data to save
	 * @param id - The ID of the inventory item (for updates only)
	 */
	const handleInventoryAction = (type: 'create' | 'update', data: InventoryInput, id?: number) => {
		if (type === 'create') {
			dispatch(actions.addInventory(data));
		} else {
			dispatch(actions.updateInventory(id!, data));
		}
	};

	/**
	 * Handle inventory item deletion action
	 * Dispatches delete action for the selected inventory item
	 */
	const handleDelete = () => {
		dispatch(actions.deleteInventory(checked[0]));
	};

	/**
	 * Handle closing the add/edit inventory item modal
	 * Resets selection if an item was being edited
	 */
	const handleAddItemModalOnClose = useCallback(() => {
		if (checkedItem) {
			setCheckedItem(undefined);
			toggleShowAddNewItemModal();
		} else {
			toggleShowAddNewItemModal();
		}
	}, [checkedItem]);

	// Set up the top navigation bar with title
	useComprehensiveTopNav('Inventory');

	// Show loading indicator while data is being fetched
	if (!isDataFetched && fetchedCount < 1) return <LoadingIndicator />;

	return (
		<>
			<AddItemModal
				inventory={checkedItem}
				vendors={vendors.list}
				isOpen={showAddNewItemModal}
				onClose={handleAddItemModalOnClose}
				action={handleInventoryAction}
			/>
			<div
				className={classes['row']}
				style={{
					justifyContent: 'space-between',
				}}
			>
				<div
					style={{
						flex: 1,
						marginRight: isSmallerDevice ? 0 : '2rem',
					}}
				>
					<SearchBar
						query={query}
						setQuery={setQuery}
						onSearch={() => console.log({ query })}
						className={classes['inventories-searchbar']}
						placeholder="Search..."
						searchIconColor="primary"
					/>
				</div>
				<div
					className={classes['row']}
					style={{
						marginTop: '1.3rem',
					}}
				>
					{canCreateInventory && (
						<button
							className={`${classes['row']} ${classes['add-new']}`}
							onClick={handleAddInventoryOnClick}
						>
							<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Add new</Typography>
							<div>
								<Image src={'icPlusPrimary'} />
							</div>
						</button>
					)}
				</div>
			</div>
			<div
				className={classes['row']}
				style={{
					marginTop: '0.1rem',
				}}
			>
				<Typography
					variant={isSmallerDevice ? 'h5' : 'h6'}
					style={{
						margin: isSmallerDevice ? '0.5rem 0.5rem 0 0' : '1rem 1rem 0 0',
					}}
					weight="medium"
				>
					Filter by:
				</Typography>
				<div style={{ marginTop: '1rem', marginRight: '0.5rem' }}>
					<DropdownButton
						title="Vendor"
						list={vendorDropdownItems}
						onItemSelect={handleVendorDropdownItemSelect}
					/>
				</div>
				<div style={{ marginTop: '1rem', marginRight: '0.5rem' }}>
					<DropdownButton
						title="Stock Status"
						list={[
							{ label: 'Stock Status 1', value: 'stock1' },
							{ label: 'Stock Status 2', value: 'stock2' },
							{ label: 'Stock Status 3', value: 'stock3' },
						]}
					/>
				</div>
			</div>
			<div className={classes['row']} style={{ justifyContent: 'space-between' }}>
				<div className={classes['row']} style={{ marginTop: isSmallerDevice ? 0 : '0.5rem' }}>
					{checked.length !== 0 && (
						<>
							{canDeleteInventory && (
								<button
									className={classes['button']}
									style={{
										backgroundColor: '#FCD3D1',
										borderColor: '#FF3B30',
									}}
									onClick={handleDelete}
								>
									<Image
										src="icTrashDanger"
										style={{
											marginRight: '0.5rem',
										}}
									/>
									<Typography variant={isSmallerDevice ? 'h5' : 'h6'} color="danger">
										Delete
									</Typography>
								</button>
							)}
						</>
					)}
				</div>
				{/* <div className={classes['row']} style={{ marginTop: isSmallerDevice ? 0 : '0.5rem' }}>
					<button
						className={classes['button']}
						style={{
							minWidth: 70,
							backgroundColor: '#E4F4F6',
							borderColor: '#A6E5E9',
							marginRight: '1rem',
							justifyContent: 'space-evenly',
						}}
					>
						<Image
							src="icExport"
							style={{
								marginRight: '0.5rem',
							}}
						/>
						<Typography variant={isSmallerDevice ? 'h5' : 'h6'} weight="light">
							Export
						</Typography>
					</button>
				</div> */}
			</div>
			<InventoriesDataTable
				inventory={paginatedData}
				checkedId={checked[0] ?? -1}
				checkAction={handleCheckAction}
				onRowClick={handleRowClick}
			/>
			<Pagination {...paginationProps} />
		</>
	);
};

export default Home;
